// 통신 실패를 사용자에게 보이게 한다 (2026-09-02 신설)
//
// 왜 — 전수조사에서 화면 123곳(FRONT 22 / ADMIN 83 / SHOP 18)이 `.then()` 만 쓰고
//   `.catch()` 가 없는 걸 확인했다. 통신이 실패하면 **아무 일도 일어나지 않는다.**
//   사용자 눈에는 "버튼이 안 먹는다"로 보이고, 저장·취소·환불처럼 되돌리기 어려운
//   동작에서는 성공한 줄 알고 지나가기까지 한다.
//   호출부 123곳을 하나씩 고치는 대신 axios 인터셉터 한 곳에서 알린다.
//
// ⚠️ Vue·스토어·라우터에 의존하지 않는다 — 인터셉터에는 컴포넌트 컨텍스트가 없다.
//   그래서 DOM 에 직접 띄운다. alert() 를 쓰지 않는 이유는 폴링 화면(어드민 입금·상담)에서
//   서버가 죽으면 모달이 끝없이 쌓여 화면을 못 쓰게 되기 때문이다.

const SUPPRESS_MS = 15000; // 같은 문구는 이 시간 안에 다시 띄우지 않는다(폴링 도배 방지)
const VISIBLE_MS = 6000;
const BOX_ID = 'gny-api-notice';

let lastMessage = '';
let lastAt = 0;
let hideTimer = null;

// 서버가 응답을 준 경우엔 그 문구를 우선한다 — 08-25 오류응답 JSON 규약(app.js 미들웨어).
function serverMessage(error) {
    const d = error && error.response && error.response.data;
    if (!d) return '';
    if (typeof d === 'string') return d.length <= 120 ? d : '';
    return String(d.message || d.msg || '').slice(0, 120);
}

export function messageFor(error) {
    // 사용자가 취소했거나 화면을 떠난 요청은 알리지 않는다.
    if (!error || error.__CANCEL__ || error.code === 'ERR_CANCELED') return '';

    const res = error.response;
    const s = Number(res && res.status);

    // ⚠️ 응답이 '있는데도' 네트워크 오류인 경우가 있다 — axios 가 status 0 짜리 응답을
    //   붙여 보낸다(연결 거부·CORS 차단·중간 끊김). 09-02 운영에서 실측으로 확인했다.
    //   `!res` 만 보고 갈랐더니 status 0 이 아래 4xx/5xx 분기를 전부 빠져나가
    //   **빈 문구가 돌아가 안내가 안 떴다.** 유효한 HTTP 상태가 아니면 연결 문제로 본다.
    if (!res || !s || s < 100) {
        if (error.code === 'ECONNABORTED') return '요청 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.';
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            return '인터넷 연결이 끊겼습니다. 연결을 확인해주세요.';
        }
        return '서버에 연결하지 못했습니다. 잠시 후 다시 시도해주세요.';
    }

    if (s === 429) return '요청이 너무 많습니다. 잠시 후 다시 시도해주세요.';
    if (s === 403) return serverMessage(error) || '권한이 없습니다.';
    if (s === 404) return serverMessage(error) || '요청한 기능을 찾을 수 없습니다.';
    if (s >= 500) return '일시적인 서버 오류입니다. 잠시 후 다시 시도해주세요.';
    if (s >= 400) return serverMessage(error) || '요청을 처리하지 못했습니다.';
    return '';
}

function ensureBox() {
    if (typeof document === 'undefined' || !document.body) return null;
    let box = document.getElementById(BOX_ID);
    if (box) return box;

    box = document.createElement('div');
    box.id = BOX_ID;
    box.setAttribute('role', 'alert');
    // 인라인 스타일 — 저장소마다 CSS 가 달라 클래스에 기댈 수 없다.
    // z-index 는 최상단. 어드민 고정헤더(1030)·모달(1055)·SweetAlert(1060) 위에 떠야 한다.
    box.style.cssText = [
        'position:fixed',
        'left:50%',
        'bottom:24px',
        'transform:translateX(-50%)',
        'max-width:min(92vw,420px)',
        'padding:12px 18px',
        'border-radius:10px',
        'background:rgba(28,30,33,.94)',
        'color:#fff',
        'font-size:14px',
        'line-height:1.5',
        'text-align:center',
        'box-shadow:0 6px 24px rgba(0,0,0,.28)',
        'z-index:2147483000',
        'pointer-events:none',
        'white-space:pre-line',
    ].join(';');
    document.body.appendChild(box);
    return box;
}

// 문구를 직접 띄운다(성공/실패 무관하게 안내가 필요할 때도 쓸 수 있다).
export function showNotice(message) {
    const text = String(message || '').trim();
    if (!text) return;

    const now = Date.now();
    if (text === lastMessage && now - lastAt < SUPPRESS_MS) return;
    lastMessage = text;
    lastAt = now;

    const box = ensureBox();
    if (!box) return;

    box.textContent = text;
    box.style.display = 'block';
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(() => {
        const el = document.getElementById(BOX_ID);
        if (el) el.style.display = 'none';
    }, VISIBLE_MS);
}

// axios 인터셉터의 오류 경로에서 부른다.
export function notifyApiError(error) {
    // 호출부가 자체 안내를 하는 요청은 `config.quiet = true` 로 끌 수 있다.
    if (error && error.config && error.config.quiet) return;
    showNotice(messageFor(error));
}

export default { showNotice, notifyApiError, messageFor };
